import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Upload } from '@factorialco/f0-react/icons/app'
import {
  FinanceContactDocumentsDataCollectionDocument,
  FinanceContactDocumentsDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import DeleteDocumentsModal from './modals/DeleteDocumentsModal'

interface ContactDocumentsDataCollectionProps {
  contactId: number
  canManageDocuments: boolean
  onClickUpload: () => void
}

const ContactDocumentsDataCollection = ({
  contactId,
  canManageDocuments,
  onClickUpload,
}: ContactDocumentsDataCollectionProps) => {
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([])

  const handleOnClickDelete = () => {
    track(trackingEvents.accounting.contactDocuments.clickOnDeleteFile)
    setIsOpenModal(true)
  }

  const handleOnCloseModal = () => {
    setIsOpenModal(false)
  }

  const source = useGraphqlDataCollectionSource({
    document: FinanceContactDocumentsDataCollectionDocument,
    mapData: (data) => {
      return data.finance.contactDocumentsConnection.nodes
    },
    mapVariables: ({ sortings }) => ({
      filterOutDeleted: true,
      contactId,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.finance.contactDocumentsConnection.totalCount ?? 0,
    }),
    itemOnClick:
      ({ url }) =>
      () => {
        track(trackingEvents.accounting.contactDocuments.clickOnDownloadFile)
        window.open(buildUrl(url), '_blank')
      },
    itemActions: ({ id }) => {
      return canManageDocuments
        ? [
            {
              label: i18n.t('common.delete'),
              icon: Delete,
              onClick: () => {
                setSelectedItemIds([id])
                handleOnClickDelete()
              },
            },
          ]
        : []
    },

    primaryActions: () =>
      canManageDocuments
        ? {
            label: i18n.t('finance.contacts.files.upload_files'),
            icon: Upload,
            onClick: () => {
              track(trackingEvents.accounting.contactDocuments.clickOnUploadFile)
              onClickUpload()
            },
          }
        : undefined,
    selectable: (item: FinanceContactDocumentsDataCollectionItemFragment) => item.id,
    bulkActions: () => {
      return canManageDocuments
        ? {
            primary: [
              {
                id: 'delete-documents',
                label: i18n.t('common.delete'),
                icon: Delete,
                critical: true,
              },
            ],
          }
        : { primary: [] }
    },
    sortings: {
      name: {
        label: i18n.t('finance.contacts.files.columns.name'),
      },
      createdOn: {
        label: i18n.t('finance.contacts.files.columns.created_on'),
      },
    },
    defaultSortings: {
      field: 'createdOn',
      order: 'desc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('finance.contacts.files.empty_state.title'),
            description: i18n.t('finance.contacts.files.empty_state.description'),
            emoji: '📄',
            actions: canManageDocuments
              ? [
                  {
                    label: i18n.t('finance.contacts.files.upload_first_file'),
                    variant: 'default',
                    onClick: () => {
                      onClickUpload()
                    },
                  },
                ]
              : [],
          },
        }}
        onBulkAction={(action, selectedItems) => {
          setSelectedItemIds(
            selectedItems.itemsStatus
              .filter((status) => status.checked)
              .map((status) => status.item.id)
          )

          switch (action) {
            case 'delete-documents':
              handleOnClickDelete()
              break
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('finance.contacts.files.columns.name'),
                  render: (item: FinanceContactDocumentsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.name,
                  }),
                  sorting: 'name',
                },
                {
                  label: i18n.t('finance.contacts.files.columns.uploader'),
                  render: (item: FinanceContactDocumentsDataCollectionItemFragment) => ({
                    type: 'person',
                    value: {
                      firstName: item.uploader?.firstName ?? '',
                      lastName: item.uploader?.lastName ?? '',
                      src: buildUrl(item.uploader?.access?.avatar),
                    },
                  }),
                },
                {
                  label: i18n.t('finance.contacts.files.columns.created_on'),
                  render: (item: FinanceContactDocumentsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: new Date(item.createdOn),
                  }),
                  sorting: 'createdOn',
                },
              ]),
            },
          },
        ]}
      />
      {isOpenModal && (
        <DeleteDocumentsModal
          onClose={handleOnCloseModal}
          documentIds={selectedItemIds}
          contactId={contactId}
        />
      )}
    </>
  )
}

export default ContactDocumentsDataCollection
